import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';

const formSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(100, 'Name must be less than 100 characters'),
  email: z.string().trim().email('Please enter a valid email address').max(255, 'Email must be less than 255 characters'),
  company: z.string().trim().max(100, 'Company name must be less than 100 characters').optional(),
  projectType: z.string().min(1, 'Please select a plan'),
  description: z.string().trim().max(2000, 'Description must be less than 2000 characters').optional(),
  timeline: z.string().min(1, 'Please select your preferred timeline'),
});

type FormData = z.infer<typeof formSchema>;

export const ContactFormSection = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Get URL params to preselect plan
  const urlParams = new URLSearchParams(window.location.search);
  const preselectedPlan = urlParams.get('plan');
  
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      projectType: preselectedPlan || undefined
    }
  });

  // Set the preselected plan when component mounts
  React.useEffect(() => {
    if (preselectedPlan) {
      setValue("projectType", preselectedPlan);
    }
  }, [preselectedPlan, setValue]);

  const projectType = watch('projectType');

  const onSubmit = async (data: FormData) => {
    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: data.name,
          email: data.email,
          company: data.company,
          plan: data.projectType,
          timeline: data.timeline,
          description: data.description,
        },
      });

      if (error) {
        throw error;
      }

      // Conversion event (GA4 + first-party). Mark `generate_lead` as a
      // conversion in the GA4 admin UI to count it toward conversions.
      trackEvent('generate_lead', {
        form_location: 'contact_form_section',
        plan: data.projectType,
        timeline: data.timeline,
        value: 1,
        currency: 'USD',
      });

      // Navigate to thank you page with plan info
      navigate(`/thank-you${data.projectType ? `?plan=${encodeURIComponent(data.projectType)}` : ''}`);
    } catch (error: any) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again or email us directly at post@affinityaim.com",
        variant: "destructive",
      });
    }
  };

  return (
    <section className="section">
      <div className="container-professional">
        {/* Header */}
        <div className="section-header-professional">
          <div className="micro-label-professional">CONTACT</div>
          <h2 className="heading-section">
            Let's build your app idea
          </h2>
          <p className="text-body max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Tell us about your project and we'll provide a detailed proposal within 24 hours
          </p>
        </div>

        <div ref={elementRef} className="max-w-2xl mx-auto">
          <form 
            onSubmit={handleSubmit(onSubmit)} 
            className="bg-[#111111] border border-[rgba(255,255,255,0.15)] rounded-xl p-8 space-y-6"
            style={getRevealStyle(0)}
          >
            {/* Name & Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-white">
                  Name *
                </Label>
                <Input
                  id="name"
                  {...register("name")}
                  className="h-11 text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] transition-all text-white"
                  placeholder="Your name"
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="text-xs text-red-400">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-white">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...register("email")}
                  className="h-11 text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] transition-all text-white"
                  placeholder="your@email.com"
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="text-xs text-red-400">{errors.email.message}</p>
                )}
              </div>
            </div>

            {/* Company */}
            <div className="space-y-2">
              <Label htmlFor="company" className="text-sm font-medium text-white">
                Company name
              </Label>
              <Input
                id="company"
                {...register("company")}
                className="h-11 text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] transition-all text-white"
                placeholder="Your company"
              />
            </div>

            {/* Project Type */}
            <div className="space-y-2">
              <Label htmlFor="projectType" className="text-sm font-medium text-white">
                Which plan interests you? *
              </Label>
              <Select value={projectType} onValueChange={(value) => setValue("projectType", value)}>
                <SelectTrigger className="h-11 text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] transition-all text-white">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent className="bg-[#111111] border-[rgba(255,255,255,0.15)]">
                  <SelectItem value="Website Essentials ($500/year)">Website Essentials ($500/year)</SelectItem>
                  <SelectItem value="Growth Website + SEO ($800/year)">Growth Website + SEO ($800/year)</SelectItem>
                  <SelectItem value="AI Chatbot ($500/year)">AI Chatbot ($500/year)</SelectItem>
                  <SelectItem value="Website + SEO + Chatbot ($1,000/year)">Website + SEO + Chatbot ($1,000/year)</SelectItem>
                  <SelectItem value="Custom App / Web App / MVP (Custom)">Custom App / Web App / MVP (Custom)</SelectItem>
                  <SelectItem value="help-decide">Help me decide</SelectItem>
                </SelectContent>
              </Select>
              {errors.projectType && (
                <p id="project-type-error" className="text-xs text-red-400">{errors.projectType.message}</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium text-white">
                Brief description of your goals
              </Label>
              <Textarea
                id="description"
                {...register("description")}
                className="text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] min-h-[100px] transition-all resize-none text-white"
                placeholder="Tell us what you're trying to achieve..."
              />
            </div>

            {/* Timeline */}
            <div className="space-y-2">
              <Label htmlFor="timeline" className="text-sm font-medium text-white">
                Preferred timeline *
              </Label>
              <Select onValueChange={(value) => setValue("timeline", value)}>
                <SelectTrigger className="h-11 text-sm bg-[#0a0a0a] border-[rgba(255,255,255,0.15)] focus:border-[#00e5ff] focus:ring-1 focus:ring-[#00e5ff] transition-all text-white">
                  <SelectValue placeholder="Select timeline" />
                </SelectTrigger>
                <SelectContent className="bg-[#111111] border-[rgba(255,255,255,0.15)]">
                  <SelectItem value="asap">ASAP</SelectItem>
                  <SelectItem value="1-2-months">1-2 months</SelectItem>
                  <SelectItem value="3-6-months">3-6 months</SelectItem>
                  <SelectItem value="just-exploring">Just exploring</SelectItem>
                </SelectContent>
              </Select>
              {errors.timeline && (
                <p id="timeline-error" className="text-xs text-red-400">{errors.timeline.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 text-sm bg-[#00e5ff] hover:bg-[#00d4e6] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-all duration-200 hover:transform hover:-translate-y-0.5"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <span>Send message</span>
                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
                  </>
                )}
              </Button>
            </div>

            {/* Reply info */}
            <p className="text-xs text-center" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              We'll reply within 24 hours from <span style={{ color: '#00e5ff' }}>post@affinityaim.com</span>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};
